/**
 * Component show the information about the points earned in the purchase
 *
 * @attr {String} [points-earned-label] - The text used as lable for earned loyalty points
 * @attr {String} [points-balance-label] - The text used as lable for loyalty points balance
 * @attr {String} [card-number-label] - The text used to name card number
 * @attr {String} [points-label] - The text used to name the points
 * @attr {String} [order-code] - Order code, is needed for loading order information
 *
 * Parameters - comes from redux store:
 *
 * @param {Number} totalPoints - The number of total points earned in the current order.
 * @param {Number} userPoints - The number of currently available loyalty points
 * @param {String} cardNumber - The user's loyalty card number
 * @param {Boolean} [hidden] - Show component
 *
 * HTML Structure Example
 *
 * <e2-loyalty-summary
 *      points-earned-label="Points earned"
 *      points-balance-label="Points"
 *      card-number-label="Card number"
 *      points-label="points"
 *      order-code= "12341234"
 * ></e2-loyalty-summary>
 */

import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors } from '@elab/core/store/cart';
import { selectors as userSelectors } from '@elab/core/store/user';
import { selectors as globalSelectors } from '@elab/core/store/global';
import { convertPointsFormatAccordingToLocale } from '@elab/core/utils/PriceUtils';

const className = 'loyalty-summary';

export class LoyaltySummary extends SimpleLitElement {
    static get properties() {
        return {
            // From redux
            totalPoints: { type: Number },
            userPoints: { type: Number },
            cardNumber: { type: String },
            hidden: { type: Boolean, reflect: true },
            locale: { type: String },

            // From attributes
            pointsEarned: { type: String, attribute: 'points-earned-label' },
            pointsBalance: { type: String, attribute: 'points-balance-label' },
            cardNumberLabel: { type: String, attribute: 'card-number-label' },
            pointsLabel: { type: String, attribute: 'points-label' },
            orderCode: { type: String, attribute: 'order-code' },
        };
    }

    afterConnect() {
        if (!this.orderCode) {
            return;
        }
        this.dispatchEvent(new CustomEvent('load-order'));
    }

    render() {
        if (this.hidden) return html``;

        return html`
            <div class="${className}">
                <h5 class="${className}__title">${this.pointsEarned}</h5>
                <h4 class="${className}__total-points">
                    ${convertPointsFormatAccordingToLocale(this.locale, this.totalPoints)}
                    ${this.pointsLabel}
                </h4>
                <hr class="${className}__line" />
                <h5 class="${className}__title">${this.pointsBalance}</h5>
                <h4 class="${className}__text">
                    ${this.userPoints} ${this.pointsLabel}
                </h4>
                <hr class="${className}__line" />
                <h5 class="${className}__title">${this.cardNumberLabel}</h5>
                <h4 class="${className}__text">${this.cardNumber}</h4>
            </div>
        `;
    }
}

const mapStateProps = state => {
    const cart = selectors.getData(state);
    const isLoyalty = userSelectors.isLoyalty(state);

    return {
        totalPoints: cart.rewardLoyaltyPoints || 0,
        cardNumber: userSelectors.getLoyaltyInformation(state).cardNumber || '',
        userPoints: cart.pointTotalAfterOrder || 0,
        hidden: userSelectors.isAnonymous(state) || !isLoyalty,
        locale: globalSelectors.getLanguage(state) || 'NL',
    };
};

export default () =>
    customElements.define('e2-loyalty-summary', connect(mapStateProps)(LoyaltySummary));
